import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { homePathForRole } from '../roleHome';

type Mode = 'login' | 'register' | 'forgot';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'BIDDER' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { name: form.name });
        setMessage('If that account exists, a password reset link has been sent to the registered email.');
        return;
      }
      if (mode === 'register') {
        // Registration no longer signs the user in: accounts are created inactive and a
        // superadmin must activate them. There is no token to store, so return to the login
        // tab with the server's explanation rather than navigating anywhere.
        const { data } = await api.post('/auth/register', form);
        setMode('login');
        setForm({ email: '', name: form.name, password: '', role: 'BIDDER' });
        setMessage(data.message ?? 'Account created. An administrator must activate it before you can log in.');
        return;
      }
      const { data } = await api.post('/auth/login', form);
      login(data.token, data.user);
      navigate(homePathForRole(data.user.role));
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Something went wrong');
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Auction Platform</h1>

        {mode !== 'forgot' && (
          <div style={styles.tabs}>
            <button style={mode === 'login' ? styles.activeTab : styles.tab} onClick={() => { setMode('login'); setError(''); setMessage(''); }}>Login</button>
            <button style={mode === 'register' ? styles.activeTab : styles.tab} onClick={() => { setMode('register'); setError(''); setMessage(''); }}>Register</button>
          </div>
        )}

        {mode === 'forgot' && (
          <h3 style={{ margin: '0 0 1rem', color: '#555', fontWeight: 500 }}>Reset Password</h3>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <input data-testid="login-name" style={styles.input} placeholder="Name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} required />
          {mode === 'register' && (
            <input style={styles.input} type="email" placeholder="Email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} required />
          )}
          {mode !== 'forgot' && (
            <input data-testid="login-password" style={styles.input} type="password" placeholder="Password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} required />
          )}
          {mode === 'register' && (
            <select style={styles.input} value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}>
              <option value="BIDDER">Bidder</option>
              <option value="AUCTIONEER">Auctioneer</option>
            </select>
          )}
          {mode === 'register' && (
            <p data-testid="register-approval-hint" style={styles.hint}>
              New accounts must be activated by an administrator before they can be used.
            </p>
          )}
          {error && <p style={styles.error}>{error}</p>}
          {message && <p data-testid="form-message" style={styles.success}>{message}</p>}
          <button data-testid="login-submit" style={styles.button} type="submit">
            {mode === 'login' ? 'Login' : mode === 'register' ? 'Register' : 'Send Reset Link'}
          </button>
        </form>

        {mode === 'login' && (
          <button style={styles.forgotLink} onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}>
            Forgot password?
          </button>
        )}
        {mode === 'forgot' && (
          <button style={styles.forgotLink} onClick={() => { setMode('login'); setError(''); setMessage(''); }}>
            Back to login
          </button>
        )}
      </div>

      <p data-testid="landing-disclaimer" style={styles.disclaimer}>
        This clock auction management system is designed and operated for research and learning
        purposes only. Lots do not represent any actual assets and bids are not binding. Only users
        explicitly authorized by the system operator are permitted to use the system. The system
        operator makes no warranties and accepts no liability for damages caused by using the system.
      </p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    minHeight: '100vh', padding: '2rem 1rem', boxSizing: 'border-box', background: '#f0f2f5',
  },
  card: { background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', width: '360px' },
  title: { textAlign: 'center', marginBottom: '1.5rem', color: '#1a1a2e' },
  tabs: { display: 'flex', marginBottom: '1.5rem' },
  tab: { flex: 1, padding: '0.5rem', border: '1px solid #ddd', background: 'white', cursor: 'pointer' },
  activeTab: { flex: 1, padding: '0.5rem', border: '1px solid #4a90e2', background: '#4a90e2', color: 'white', cursor: 'pointer' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  input: { padding: '0.6rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' },
  button: { padding: '0.75rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer' },
  error: { color: 'red', fontSize: '0.875rem', margin: 0 },
  success: { color: '#2e7d32', fontSize: '0.875rem', margin: 0 },
  hint: { color: '#666', fontSize: '0.8rem', margin: 0, lineHeight: 1.4 },
  forgotLink: { marginTop: '0.75rem', background: 'none', border: 'none', color: '#4a90e2', cursor: 'pointer', fontSize: '0.875rem', padding: 0, width: '100%', textAlign: 'center' },
  // 424px = the card's rendered width (360px content-box + its 2rem padding either side),
  // so the notice sits flush with the card's edges. There is no global box-sizing reset.
  disclaimer: {
    boxSizing: 'border-box', width: '424px', maxWidth: '100%', margin: '1.25rem 0 0',
    fontSize: '0.75rem', lineHeight: 1.5, color: '#666', textAlign: 'left',
  },
};
